/**
 * Visual Analyzer - Pass 3 of Multi-Pass Analysis
 *
 * Extracts facets from visual content (images, diagrams, wireframes) using
 * Claude's vision capabilities. Integrates with entities from Pass 1.
 */

import type { ClaudeConfig } from '../ai/claude'
import { callClaudeWithImages, parseJsonResponse } from '../ai/claude'
import type { ContentType } from './types'
import type { ExtractedFacet } from './facetAnalyzer'

// ============================================================================
// TYPES
// ============================================================================

export interface ImageInput {
  id: string
  data: string      // Base64 encoded
  mimeType?: string // 'image/png' | 'image/jpeg'
  pageNumber?: number
  width?: number
  height?: number
}

export interface VisualAnalysisRequest {
  sourceId: string
  images: ImageInput[]
  contentType: ContentType
  extractedEntities?: Array<{
    id: string
    type: string
    name: string
  }>
}

export interface ClassificationResult {
  imageId: string
  category: string
  confidence: number
  shouldAnalyze: boolean
  strategy: string | null
}

export interface VisualFacet extends ExtractedFacet {
  imageId: string
  imageIndex: number
  bbox?: { x: number; y: number; width: number; height: number }
}

export interface VisualRelationship {
  fromFacetIndex: number
  toFacetIndex: number
  type: string
  label?: string
  confidence: number
}

export interface VisualAnalysisResult {
  sourceId: string
  classifications: ClassificationResult[]
  facets: VisualFacet[]
  relationships: VisualRelationship[]
  analysisTime: number
  skippedImages: number
  analyzedImages: number
  error?: string
}

// ============================================================================
// CLASSIFICATION PROMPTS
// ============================================================================

const IMAGE_CLASSIFICATION_PROMPT = `You are an image classifier for a business intelligence system.

Analyze this image and classify it into ONE of these categories:

ANALYZE THESE:
- logo: Company or brand logo
- business_canvas: Business Model Canvas, Lean Canvas, or similar strategic framework
- whiteboard: Whiteboard with sticky notes, sketches, or brainstorming content
- architecture_diagram: System architecture, infrastructure, or technical diagram
- process_diagram: Flowchart, process flow, or workflow diagram
- ui_screenshot: User interface screenshot or mockup
- wireframe: Low-fidelity UI wireframe or sketch
- chart: Bar chart, line chart, pie chart, or data visualization
- org_chart: Organizational chart or hierarchy diagram
- journey_map: Customer journey map or experience map
- swot: SWOT analysis diagram
- mindmap: Mind map or concept map
- timeline: Timeline, roadmap, or Gantt chart
- table: Data table or spreadsheet screenshot

SKIP THESE:
- decorative: Decorative image, stock photo, or illustration without business content
- icon: Small icon or symbol (< 64x64 effective size)
- photo: Photo without business relevance
- unreadable: Image too blurry or low quality to analyze

Return JSON only:
{
  "category": "string",
  "confidence": 0.0-1.0,
  "reasoning": "brief explanation",
  "should_analyze": true/false,
  "suggested_strategy": "logo|canvas|whiteboard|diagram|ui|chart|org|journey|swot|timeline|null"
}`

const IMAGE_TYPE_TO_STRATEGY: Record<string, string> = {
  'logo': 'logo',
  'business_canvas': 'canvas',
  'whiteboard': 'whiteboard',
  'architecture_diagram': 'diagram',
  'process_diagram': 'diagram',
  'ui_screenshot': 'ui',
  'wireframe': 'ui',
  'chart': 'chart',
  'org_chart': 'org',
  'journey_map': 'journey',
  'swot': 'swot',
  'mindmap': 'whiteboard',
  'timeline': 'timeline',
  'table': 'chart'
}

// ============================================================================
// ANALYSIS PROMPTS
// ============================================================================

const ANALYSIS_PROMPTS: Record<string, string> = {
  logo: `Analyze this logo image to extract business entities.

Extract:
1. Company/brand name (read text, recognize known logos)
2. Type: competitor, partner, or own company
3. Any visible tagline
4. Industry inference from visual style

Return JSON only:
{
  "entities": [
    {
      "type": "Company|Competitor|Partner",
      "name": "string",
      "confidence": 0.0-1.0,
      "tagline": "string|null",
      "industry_guess": "string|null",
      "evidence": "what in the image led to this"
    }
  ]
}`,

  canvas: `Analyze this Business Model Canvas (or similar strategic canvas).

Extract content from each visible section:
- Value Propositions, Customer Segments, Channels
- Revenue Streams, Key Resources, Key Activities
- Key Partners, Cost Structure

Return JSON only:
{
  "canvas_type": "BMC|Lean Canvas|Value Proposition Canvas|Other",
  "sections": [
    {
      "section_name": "string",
      "items": [
        {"text": "string", "entity_type": "string", "confidence": 0.0-1.0}
      ]
    }
  ]
}`,

  whiteboard: `Analyze this whiteboard/sticky note image for business insights.

Extract:
1. Individual sticky notes or written items
2. Group related items by color or clustering
3. Categorize by color: yellow=ideas, pink=problems, green=solutions, blue=actions

Return JSON only:
{
  "items": [
    {
      "text": "string (OCR content)",
      "category": "idea|problem|solution|action|question|note",
      "color": "yellow|pink|green|blue|orange|white|other",
      "confidence": 0.0-1.0
    }
  ]
}`,

  diagram: `Analyze this architecture or process diagram.

Extract:
1. All components/boxes with labels
2. Connections/arrows and directions
3. Component types (service, database, user, external)

Return JSON only:
{
  "diagram_type": "architecture|flowchart|sequence|network|other",
  "components": [
    {"label": "string", "type": "service|database|user|external|process|decision", "confidence": 0.0-1.0}
  ],
  "connections": [
    {"from_label": "string", "to_label": "string", "label": "string|null"}
  ]
}`,

  ui: `Analyze this UI screenshot or wireframe.

Extract:
1. Major UI sections/components
2. Visible text (buttons, labels, headings)
3. App type/purpose

Return JSON only:
{
  "app_type": "string",
  "platform": "web|mobile|desktop",
  "features_visible": [{"name": "string", "description": "string"}],
  "ux_observations": [{"type": "positive|negative|neutral", "observation": "string"}]
}`,

  chart: `Analyze this chart or data visualization.

Extract:
1. Chart type
2. Axis labels and legend
3. Key data points
4. Trends

Return JSON only:
{
  "chart_type": "bar|line|pie|scatter|area|other",
  "title": "string|null",
  "metrics": [{"name": "string", "value": "string|null"}],
  "trends": [{"description": "string", "direction": "up|down|flat"}]
}`,

  org: `Analyze this organizational chart.

Extract:
1. All visible roles/positions
2. Hierarchy (who reports to whom)
3. Teams/departments

Return JSON only:
{
  "roles": [
    {"title": "string", "name": "string|null", "level": 0, "reports_to": "string|null"}
  ],
  "departments": [{"name": "string", "head": "string|null"}]
}`,

  journey: `Analyze this customer journey map.

Extract:
1. Journey stages
2. Touchpoints
3. Pain points and opportunities

Return JSON only:
{
  "stages": [
    {"name": "string", "order": 0, "touchpoints": [{"name": "string", "emotion": "positive|neutral|negative"}]}
  ],
  "pain_points": [{"stage": "string", "description": "string"}],
  "opportunities": [{"stage": "string", "description": "string"}]
}`,

  swot: `Analyze this SWOT analysis diagram.

Extract items from each quadrant.

Return JSON only:
{
  "strengths": [{"text": "string"}],
  "weaknesses": [{"text": "string"}],
  "opportunities": [{"text": "string"}],
  "threats": [{"text": "string"}]
}`,

  timeline: `Analyze this timeline, roadmap, or Gantt chart.

Extract:
1. Milestones/events
2. Phases or periods
3. Dependencies

Return JSON only:
{
  "type": "timeline|roadmap|gantt",
  "items": [
    {"name": "string", "type": "milestone|phase|task", "status": "done|in_progress|planned|null"}
  ]
}`
}

// ============================================================================
// COMBINED CLASSIFY+ANALYZE PROMPT (OPTIMIZED - 1 call per image vs 2)
// ============================================================================

const COMBINED_ANALYSIS_PROMPT = `You are a visual content analyzer for a business intelligence system.

STEP 1 - CLASSIFY: First determine the image type:
ANALYZABLE: logo, business_canvas, whiteboard, architecture_diagram, process_diagram, ui_screenshot, wireframe, chart, org_chart, journey_map, swot, mindmap, timeline, table
SKIP: decorative, icon, photo, unreadable

STEP 2 - EXTRACT (only if analyzable): Extract structured data based on type.

Return JSON:
{
  "classification": {
    "category": "<type>",
    "confidence": 0.0-1.0,
    "should_analyze": true/false
  },
  "extraction": null | {
    // Type-specific data (see below)
  }
}

TYPE-SPECIFIC EXTRACTION FORMATS:

LOGO: {"entities": [{"type": "Company|Competitor|Partner", "name": "string", "tagline": "string|null"}]}

CANVAS: {"canvas_type": "BMC|Lean Canvas", "sections": [{"section_name": "string", "items": [{"text": "string", "entity_type": "string"}]}]}

WHITEBOARD: {"items": [{"text": "string", "category": "idea|problem|solution|action", "color": "yellow|pink|green|blue"}]}

DIAGRAM: {"diagram_type": "architecture|flowchart", "components": [{"label": "string", "type": "service|database|user|external"}]}

UI: {"app_type": "string", "platform": "web|mobile", "features_visible": [{"name": "string", "description": "string"}]}

CHART: {"chart_type": "bar|line|pie", "metrics": [{"name": "string", "value": "string"}], "trends": [{"description": "string", "direction": "up|down|flat"}]}

ORG: {"roles": [{"title": "string", "name": "string|null", "level": 0}]}

JOURNEY: {"stages": [{"name": "string", "touchpoints": [{"name": "string", "emotion": "positive|neutral|negative"}]}], "pain_points": [{"stage": "string", "description": "string"}]}

SWOT: {"strengths": [{"text": "string"}], "weaknesses": [{"text": "string"}], "opportunities": [{"text": "string"}], "threats": [{"text": "string"}]}

TIMELINE: {"type": "timeline|roadmap|gantt", "items": [{"name": "string", "type": "milestone|phase|task"}]}

TABLE: {"headers": ["string"], "rows": [["string"]]}

If SKIP category, set extraction to null.`

// ============================================================================
// MAIN ANALYZER
// ============================================================================

/**
 * Main entry point for visual analysis (optimized - 1 API call per image)
 */
export async function analyzeVisual(
  config: ClaudeConfig,
  request: VisualAnalysisRequest
): Promise<VisualAnalysisResult> {
  const startTime = Date.now()

  if (!request.images || request.images.length === 0) {
    return {
      sourceId: request.sourceId,
      classifications: [],
      facets: [],
      relationships: [],
      analysisTime: 0,
      skippedImages: 0,
      analyzedImages: 0
    }
  }

  console.log(`[visualAnalyzer] Starting optimized analysis of ${request.images.length} images (1 call per image)`)

  try {
    const classifications: ClassificationResult[] = []
    const allFacets: VisualFacet[] = []
    const allRelationships: VisualRelationship[] = []
    let skipped = 0
    let analyzed = 0

    // Process each image with combined classify+analyze (1 call per image)
    for (const image of request.images) {
      const result = await classifyAndAnalyzeImage(config, image)

      classifications.push({
        imageId: image.id,
        category: result.classification.category,
        confidence: result.classification.confidence,
        shouldAnalyze: result.classification.should_analyze,
        strategy: IMAGE_TYPE_TO_STRATEGY[result.classification.category] || null
      })

      if (result.classification.should_analyze && result.extraction) {
        analyzed++
        const strategy = IMAGE_TYPE_TO_STRATEGY[result.classification.category]
        if (strategy) {
          const { facets, relationships } = convertToFacets(result.extraction, strategy, image)
          allFacets.push(...facets)
          allRelationships.push(...relationships)
        }
      } else {
        skipped++
      }
    }

    console.log(`[visualAnalyzer] ${analyzed} images analyzed, ${skipped} skipped`)

    // Deduplicate is now handled by separate deduplication module
    // For now, keep inline dedup for backward compatibility
    const deduplicatedFacets = deduplicateAgainstTextEntities(
      allFacets,
      request.extractedEntities || []
    )

    console.log(`[visualAnalyzer] Extracted ${deduplicatedFacets.length} visual facets`)

    return {
      sourceId: request.sourceId,
      classifications,
      facets: deduplicatedFacets,
      relationships: allRelationships,
      analysisTime: Date.now() - startTime,
      skippedImages: skipped,
      analyzedImages: analyzed
    }
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Unknown error'
    console.error('[visualAnalyzer] Error:', message)
    return {
      sourceId: request.sourceId,
      classifications: [],
      facets: [],
      relationships: [],
      analysisTime: Date.now() - startTime,
      skippedImages: 0,
      analyzedImages: 0,
      error: message
    }
  }
}

/**
 * Combined classify and analyze in single API call (OPTIMIZED)
 */
async function classifyAndAnalyzeImage(
  config: ClaudeConfig,
  image: ImageInput
): Promise<{
  classification: { category: string; confidence: number; should_analyze: boolean }
  extraction: Record<string, unknown> | null
}> {
  try {
    const response = await callClaudeWithImages(
      { ...config, maxTokens: 4000 },
      COMBINED_ANALYSIS_PROMPT,
      'Classify and analyze this image.',
      [{
        type: 'base64',
        data: image.data,
        mediaType: (image.mimeType || 'image/png') as 'image/png' | 'image/jpeg' | 'image/gif' | 'image/webp'
      }]
    )

    const result = parseJsonResponse<{
      classification: { category: string; confidence: number; should_analyze: boolean }
      extraction: Record<string, unknown> | null
    }>(response)

    if (result) {
      return result
    }

    // Fallback on parse error
    return {
      classification: { category: 'error', confidence: 0, should_analyze: false },
      extraction: null
    }
  } catch (error) {
    console.error(`[visualAnalyzer] Failed to analyze image ${image.id}:`, error)
    return {
      classification: { category: 'error', confidence: 0, should_analyze: false },
      extraction: null
    }
  }
}

/**
 * Classify images to determine analysis strategy
 */
async function classifyImages(
  config: ClaudeConfig,
  images: ImageInput[]
): Promise<ClassificationResult[]> {
  const results: ClassificationResult[] = []

  // Process images one at a time to avoid rate limits
  for (const image of images) {
    try {
      const response = await callClaudeWithImages(
        { ...config, maxTokens: 500 },
        IMAGE_CLASSIFICATION_PROMPT,
        'Classify this image.',
        [{
          type: 'base64',
          data: image.data,
          mediaType: (image.mimeType || 'image/png') as 'image/png' | 'image/jpeg' | 'image/gif' | 'image/webp'
        }]
      )

      const result = parseJsonResponse<{
        category: string
        confidence: number
        should_analyze: boolean
      }>(response)

      if (result) {
        results.push({
          imageId: image.id,
          category: result.category,
          confidence: result.confidence,
          shouldAnalyze: result.should_analyze,
          strategy: IMAGE_TYPE_TO_STRATEGY[result.category] || null
        })
      } else {
        results.push({
          imageId: image.id,
          category: 'error',
          confidence: 0,
          shouldAnalyze: false,
          strategy: null
        })
      }
    } catch (error) {
      console.error(`[visualAnalyzer] Failed to classify image ${image.id}:`, error)
      results.push({
        imageId: image.id,
        category: 'error',
        confidence: 0,
        shouldAnalyze: false,
        strategy: null
      })
    }
  }

  return results
}

/**
 * Analyze image with specific strategy
 */
async function analyzeImageWithStrategy(
  config: ClaudeConfig,
  image: ImageInput,
  strategy: string,
  entityContext?: Array<{ id: string; type: string; name: string }>
): Promise<{ facets: VisualFacet[]; relationships: VisualRelationship[] }> {
  const prompt = ANALYSIS_PROMPTS[strategy]

  if (!prompt) {
    console.warn(`[visualAnalyzer] No prompt for strategy: ${strategy}`)
    return { facets: [], relationships: [] }
  }

  // Add entity context to prompt
  let enhancedPrompt = prompt
  if (entityContext && entityContext.length > 0) {
    const contextSummary = entityContext
      .slice(0, 15)
      .map(e => `- ${e.type}: ${e.name}`)
      .join('\n')

    enhancedPrompt += `\n\nCONTEXT: These entities were found in the document. Look for visual confirmation:\n${contextSummary}`
  }

  try {
    const response = await callClaudeWithImages(
      { ...config, maxTokens: 4000 },
      enhancedPrompt,
      'Analyze this image and extract structured data.',
      [{
        type: 'base64',
        data: image.data,
        mediaType: (image.mimeType || 'image/png') as 'image/png' | 'image/jpeg' | 'image/gif' | 'image/webp'
      }]
    )

    const result = parseJsonResponse<Record<string, unknown>>(response)
    if (!result) return { facets: [], relationships: [] }

    return convertToFacets(result, strategy, image)
  } catch (error) {
    console.error(`[visualAnalyzer] Failed to analyze image:`, error)
    return { facets: [], relationships: [] }
  }
}

/**
 * Convert strategy-specific result to unified facets
 */
function convertToFacets(
  result: Record<string, unknown>,
  strategy: string,
  image: ImageInput
): { facets: VisualFacet[]; relationships: VisualRelationship[] } {
  const facets: VisualFacet[] = []
  const relationships: VisualRelationship[] = []

  switch (strategy) {
    case 'logo':
      for (const entity of (result.entities as Array<Record<string, unknown>>) || []) {
        facets.push(createFacet(
          `detected_${String(entity.type).toLowerCase()}`,
          String(entity.name),
          String(entity.tagline || entity.industry_guess || ''),
          String(entity.evidence || ''),
          Number(entity.confidence) || 0.7,
          String(entity.type),
          image
        ))
      }
      break

    case 'canvas':
      for (const section of (result.sections as Array<Record<string, unknown>>) || []) {
        for (const item of (section.items as Array<Record<string, unknown>>) || []) {
          facets.push(createFacet(
            `${String(section.section_name).toLowerCase().replace(/\s+/g, '_')}_visual`,
            String(item.text),
            `From ${section.section_name} section`,
            `Canvas: ${result.canvas_type}`,
            Number(item.confidence) || 0.7,
            String(item.entity_type),
            image
          ))
        }
      }
      break

    case 'whiteboard':
      for (const item of (result.items as Array<Record<string, unknown>>) || []) {
        facets.push(createFacet(
          `sticky_${String(item.category)}`,
          String(item.text),
          `${item.color} sticky note`,
          `Whiteboard item`,
          Number(item.confidence) || 0.7,
          categoryToEntityType(String(item.category)),
          image
        ))
      }
      break

    case 'diagram':
      for (const comp of (result.components as Array<Record<string, unknown>>) || []) {
        facets.push(createFacet(
          componentTypeToFacetType(String(comp.type)),
          String(comp.label),
          `${result.diagram_type} component`,
          `Diagram (${comp.type})`,
          Number(comp.confidence) || 0.7,
          componentTypeToEntityType(String(comp.type)),
          image
        ))
      }
      break

    case 'ui':
      for (const feature of (result.features_visible as Array<Record<string, unknown>>) || []) {
        facets.push(createFacet(
          'ui_feature',
          String(feature.name),
          String(feature.description),
          `UI: ${result.app_type} (${result.platform})`,
          0.75,
          'Feature',
          image
        ))
      }
      break

    case 'chart':
      for (const metric of (result.metrics as Array<Record<string, unknown>>) || []) {
        facets.push(createFacet(
          'metric_visual',
          String(metric.name),
          String(metric.value || ''),
          `Chart: ${result.chart_type}`,
          0.7,
          'Metric',
          image
        ))
      }
      for (const trend of (result.trends as Array<Record<string, unknown>>) || []) {
        facets.push(createFacet(
          'trend_visual',
          String(trend.description),
          `Direction: ${trend.direction}`,
          `Chart trend`,
          0.7,
          'Trend',
          image
        ))
      }
      break

    case 'org':
      for (const role of (result.roles as Array<Record<string, unknown>>) || []) {
        facets.push(createFacet(
          'role_visual',
          String(role.title),
          role.name ? `${role.name}` : '',
          `Org chart (level ${role.level})`,
          0.8,
          'Stakeholder',
          image
        ))
      }
      break

    case 'journey':
      for (const stage of (result.stages as Array<Record<string, unknown>>) || []) {
        for (const tp of (stage.touchpoints as Array<Record<string, unknown>>) || []) {
          facets.push(createFacet(
            'touchpoint_visual',
            String(tp.name),
            `Stage: ${stage.name}`,
            `Journey map`,
            0.75,
            'Touchpoint',
            image
          ))
        }
      }
      for (const pain of (result.pain_points as Array<Record<string, unknown>>) || []) {
        facets.push(createFacet(
          'journey_pain',
          String(pain.description),
          `Stage: ${pain.stage}`,
          `Journey pain point`,
          0.8,
          'PainPoint',
          image
        ))
      }
      break

    case 'swot':
      for (const s of (result.strengths as Array<Record<string, unknown>>) || []) {
        facets.push(createFacet('strength_visual', String(s.text), '', 'SWOT', 0.85, 'Strength', image))
      }
      for (const w of (result.weaknesses as Array<Record<string, unknown>>) || []) {
        facets.push(createFacet('weakness_visual', String(w.text), '', 'SWOT', 0.85, 'Weakness', image))
      }
      for (const o of (result.opportunities as Array<Record<string, unknown>>) || []) {
        facets.push(createFacet('opportunity_visual', String(o.text), '', 'SWOT', 0.85, 'Opportunity', image))
      }
      for (const t of (result.threats as Array<Record<string, unknown>>) || []) {
        facets.push(createFacet('threat_visual', String(t.text), '', 'SWOT', 0.85, 'Threat', image))
      }
      break

    case 'timeline':
      for (const item of (result.items as Array<Record<string, unknown>>) || []) {
        const facetType = item.type === 'milestone' ? 'milestone_visual' : 'phase_visual'
        facets.push(createFacet(
          facetType,
          String(item.name),
          item.status ? `Status: ${item.status}` : '',
          `Timeline`,
          0.8,
          item.type === 'milestone' ? 'Milestone' : 'Phase',
          image
        ))
      }
      break
  }

  return { facets, relationships }
}

/**
 * Create a facet object
 */
function createFacet(
  facetTypeId: string,
  title: string,
  content: string,
  evidence: string,
  confidence: number,
  suggestedEntityType: string,
  image: ImageInput
): VisualFacet {
  return {
    id: crypto.randomUUID(),
    facetTypeId,
    title,
    content,
    evidence,
    confidence,
    suggestedEntityType,
    imageId: image.id,
    imageIndex: parseInt(image.id.split('_').pop() || '0', 10)
  }
}

/**
 * Deduplicate visual facets against text entities
 */
function deduplicateAgainstTextEntities(
  visualFacets: VisualFacet[],
  textEntities: Array<{ id: string; type: string; name: string }>
): VisualFacet[] {
  const textEntityNames = new Set(
    textEntities.map(e => e.name.toLowerCase().trim())
  )

  return visualFacets.map(facet => {
    const normalizedTitle = facet.title.toLowerCase().trim()

    // Check for exact or fuzzy match
    const hasTextMatch = textEntityNames.has(normalizedTitle) ||
      [...textEntityNames].some(name => similarityScore(name, normalizedTitle) > 0.85)

    if (hasTextMatch) {
      return {
        ...facet,
        content: `[Visual confirmation] ${facet.content || ''}`,
        confidence: Math.min(facet.confidence + 0.1, 1.0)
      }
    }

    return facet
  })
}

// ============================================================================
// HELPERS
// ============================================================================

function categoryToEntityType(category: string): string {
  const map: Record<string, string> = {
    'idea': 'Idea',
    'problem': 'CustomerPain',
    'solution': 'Solution',
    'action': 'ActionItem',
    'question': 'Question',
    'note': 'Note'
  }
  return map[category] || 'Note'
}

function componentTypeToFacetType(type: string): string {
  const map: Record<string, string> = {
    'service': 'system_component',
    'database': 'data_store',
    'user': 'system_component',
    'external': 'integration_point',
    'process': 'process_step',
    'decision': 'decision_point'
  }
  return map[type] || 'system_component'
}

function componentTypeToEntityType(type: string): string {
  const map: Record<string, string> = {
    'service': 'System',
    'database': 'DataStore',
    'user': 'User',
    'external': 'Integration',
    'process': 'Process',
    'decision': 'Decision'
  }
  return map[type] || 'System'
}

function similarityScore(a: string, b: string): number {
  const trigramsA = new Set(getTrigrams(a))
  const trigramsB = new Set(getTrigrams(b))

  const intersection = [...trigramsA].filter(t => trigramsB.has(t)).length
  const union = new Set([...trigramsA, ...trigramsB]).size

  return union > 0 ? intersection / union : 0
}

function getTrigrams(str: string): string[] {
  const trigrams: string[] = []
  for (let i = 0; i <= str.length - 3; i++) {
    trigrams.push(str.slice(i, i + 3))
  }
  return trigrams
}
